import { Link, useParams } from "react-router-dom";
import { MapPin, ArrowLeft, Check, FileDown } from "lucide-react";
import { useSeo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { EnquiryForm } from "@/components/EnquiryForm";
import { PROJECTS } from "@/data/content";

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const project = PROJECTS.find((p) => p.slug === slug);

  useSeo({
    title: project
      ? `${project.name} — ${project.location} | Jaybhaye Realty Pvt. Ltd.`
      : "Project | Jaybhaye Realty Pvt. Ltd.",
    description: project ? `${project.name}, ${project.location}. ${project.tagline}` : "Selected project by Jaybhaye Realty.",
    path: `/projects/${slug}`,
  });

  if (!project) {
    return (
      <div className="py-32 text-center" data-testid="project-not-found">
        <h1 className="font-monument text-3xl text-[#12281a] mb-4">Project Not Found</h1>
        <Link to="/projects" className="text-xs uppercase tracking-widest text-[#b38b47] font-semibold" data-testid="project-back-link">
          ← Back to Projects
        </Link>
      </div>
    );
  }

  return (
    <div data-testid={`project-detail-${project.slug}`}>
      {/* Project hero */}
      <section className="relative bg-[#12281a] text-[#f7f5f0]">
        {project.heroImage && (
          <div className="absolute inset-0 opacity-30">
            <img src={project.heroImage} alt="" className="w-full h-full object-cover" loading="eager" />
          </div>
        )}
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#f7f5f0]/60 hover:text-[#b38b47] transition-colors mb-8"
            data-testid="project-back-link"
          >
            <ArrowLeft size={14} /> All Projects
          </Link>
          <span className="text-xs uppercase tracking-[0.3em] text-[#b38b47] font-bold block mb-4">
            {project.type} · {project.status}
          </span>
          <h1 className="font-monument text-4xl sm:text-6xl leading-[1.02] uppercase mb-6" data-testid="project-name">
            {project.name}
          </h1>
          <p className="text-lg text-[#f7f5f0]/80 font-light max-w-2xl flex items-center gap-2">
            <MapPin size={18} className="text-[#b38b47] shrink-0" /> {project.location}
          </p>
        </div>
      </section>

      {/* Story + highlights */}
      <section className="py-24 bg-white border-b border-[#12281a]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="text-xs uppercase tracking-[0.25em] text-[#b38b47] font-semibold block mb-4">
                The Project
              </span>
              <p className="text-lg text-[#2c382f] leading-relaxed mb-10">{project.story}</p>
              <div className="text-xs uppercase tracking-widest text-[#12281a]/50 mb-2">{project.developer}</div>
              <div className="text-xs uppercase tracking-widest text-[#12281a] font-semibold">
                Status: {project.status}
              </div>
              {project.rera && (
                <div className="text-xs uppercase tracking-widest text-[#5c1822] font-semibold mt-2" data-testid="project-rera">
                  {project.rera}
                </div>
              )}
              <div className="flex flex-wrap gap-6 mt-8">
                {project.mapUrl && (
                  <a
                    href={project.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[#12281a] border-b-2 border-[#b38b47] pb-1 hover:text-[#b38b47] transition-colors"
                    data-testid="project-map-link"
                  >
                    <MapPin size={14} /> View on Google Maps
                  </a>
                )}
                {project.brochureUrl && (
                  <a
                    href={project.brochureUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[#12281a] border-b-2 border-[#b38b47] pb-1 hover:text-[#b38b47] transition-colors"
                    data-testid="project-brochure-link"
                  >
                    <FileDown size={14} /> Download Brochure (PDF)
                  </a>
                )}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={120}>
              <div className="border border-[#12281a]/15 p-8 bg-[#f7f5f0]">
                <span className="text-xs uppercase tracking-[0.25em] text-[#b38b47] font-semibold block mb-6">
                  Key Highlights
                </span>
                <ul className="space-y-4">
                  {project.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-3 text-sm text-[#2c382f]">
                      <Check size={16} className="text-[#b38b47] shrink-0 mt-0.5" />
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            {project.image && (
              <Reveal delay={200}>
                <img
                  src={project.image}
                  alt={`${project.name} — project view`}
                  className="mt-8 w-full border border-[#12281a]/15 object-cover"
                  loading="lazy"
                  data-testid="project-gallery-image"
                />
              </Reveal>
            )}
          </div>
        </div>

        {project.gallery && project.gallery.length > 0 && (
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="project-gallery">
            {project.gallery.map((src, gi) => (
              <Reveal key={src} delay={gi * 100}>
                <img
                  src={src}
                  alt={`${project.name} — gallery image ${gi + 1}`}
                  className="w-full border border-[#12281a]/15 object-cover"
                  loading="lazy"
                  data-testid={`project-gallery-${gi}`}
                />
              </Reveal>
            ))}
          </div>
        )}

        {project.videoUrl && (
          <div className="mt-16" data-testid="project-video-section">
            <span className="text-xs uppercase tracking-[0.25em] text-[#b38b47] font-semibold block mb-6">
              Site Visit Film
            </span>
            <div className="aspect-video w-full border border-[#12281a]/15 bg-[#12281a]">
              {project.videoUrl.includes("youtube.com") || project.videoUrl.includes("youtu.be") ? (
                <iframe
                  src={project.videoUrl}
                  title={`${project.name} — site visit video`}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                  data-testid="project-video-embed"
                />
              ) : (
                <video
                  src={project.videoUrl}
                  controls
                  preload="metadata"
                  className="w-full h-full object-cover"
                  data-testid="project-video-player"
                />
              )}
            </div>
          </div>
        )}
      </section>

      {/* Enquiry */}
      <section className="py-24 bg-[#12281a] text-[#f7f5f0]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="text-center mb-12">
              <span className="text-xs uppercase tracking-[0.25em] text-[#b38b47] font-semibold block mb-2">
                Enquire
              </span>
              <h2 className="text-3xl font-monument text-[#f7f5f0]">About {project.name}</h2>
              {!project.brochureUrl && (
                <p className="text-xs text-[#f7f5f0]/60 mt-3">
                  Detailed documentation available on request through our executive desk.
                </p>
              )}
            </div>
          </Reveal>
          <EnquiryForm defaultType="project-enquiry" compact />
        </div>
      </section>
    </div>
  );
}
